// ============================================================================
// PAYMENT MODES HOOK
// ============================================================================
// Reads the PAYMENT_SETTINGS / enabled_payment_modes config and returns the
// list of payment modes a property/system allows. Mirrors useAdvancePolicy.

import { useState, useEffect, useCallback } from 'react';
import { configService } from 'api/services/configService';
import { PAYMENT_MODES } from 'api/types';

const CONFIG_GROUP = 'PAYMENT_SETTINGS';
const CONFIG_KEY = 'enabled_payment_modes';
// Fallback when the config row hasn't been seeded or fetch fails — keep the
// existing hardcoded behaviour so the cashier flow never breaks.
const FALLBACK_MODES: string[] = [...PAYMENT_MODES];

export const usePaymentModes = (propertyId?: string) => {
  const [modes, setModes] = useState<string[]>(FALLBACK_MODES);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchModes = useCallback(
    async (propId?: string) => {
      setLoading(true);
      setError(null);
      try {
        const scopes: Array<{ type: string; id: string }> = propId
          ? [{ type: 'PROPERTY', id: propId }]
          : [];

        const response = await configService.getEffectiveConfig(CONFIG_GROUP, scopes);
        const item = response.data.find(v => v.configKey === CONFIG_KEY);
        const list = item?.listValues
          ?.filter(li => li.isActive !== false)
          .map(li => li.value)
          .filter(Boolean) ?? [];

        // Fall back to the catalog if the config row is absent (fresh DB before seed)
        // or returns an empty list — never leave the cashier with zero options.
        const result = list.length > 0 ? list : FALLBACK_MODES;
        setModes(result);
        return result;
      } catch (err) {
        const e = err as Error;
        setError(e);
        // On error, keep whatever we last had (or the fallback if first load).
        return modes;
      } finally {
        setLoading(false);
      }
    },
    // modes intentionally omitted — we don't want to refetch on its own change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Auto-fetch on propertyId change. When propertyId is undefined (e.g., the
  // standalone Advance Payments grid filter), the call still resolves SYSTEM scope.
  useEffect(() => {
    fetchModes(propertyId);
  }, [propertyId, fetchModes]);

  return {
    modes,
    loading,
    error,
    refetch: fetchModes,
  };
};
